import React, { useState, useEffect } from 'react';
import { Consultant, ConsultantContact, ManagedCustomer, AuthorizationFile } from '../../types/consultant';
import { createConsultant, updateConsultant } from '../../services/consultantService';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';

interface ConsultantFormProps {
  consultant: Consultant | null;
  onClose: () => void;
  isOpen: boolean;
}

const ConsultantForm: React.FC<ConsultantFormProps> = ({ consultant, onClose, isOpen }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    taxNumber: '',
    registrationNumber: '',
    address: '',
    notes: ''
  });
  const [contacts, setContacts] = useState<ConsultantContact[]>([]);
  const [managedCustomers, setManagedCustomers] = useState<ManagedCustomer[]>([]);
  const [authorizationFiles, setAuthorizationFiles] = useState<AuthorizationFile[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (consultant) {
      setFormData({
        companyName: consultant.companyName,
        taxNumber: consultant.taxNumber || '',
        registrationNumber: consultant.registrationNumber || '',
        address: consultant.address || '',
        notes: consultant.notes || ''
      });
      setContacts(consultant.contacts);
      setManagedCustomers(consultant.managedCustomers);
      setAuthorizationFiles(consultant.authorizationFiles);
    }
  }, [consultant]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.companyName.trim()) {
      toast.error('A cégnév megadása kötelező!');
      return;
    }

    try {
      setLoading(true);

      const consultantData = {
        companyName: formData.companyName,
        taxNumber: formData.taxNumber,
        registrationNumber: formData.registrationNumber,
        address: formData.address,
        contacts,
        managedCustomers,
        authorizationFiles
      };

      if (consultant) {
        await updateConsultant(consultant.id, consultantData);
        toast.success('Szakreferens sikeresen frissítve!');
      } else {
        await createConsultant(consultantData);
        toast.success('Szakreferens sikeresen létrehozva!');
      }

      onClose();
    } catch (error) {
      console.error('Error saving consultant:', error);
      toast.error('Hiba a szakreferens mentése során');
    } finally {
      setLoading(false);
    }
  };

  const addContact = () => {
    const newContact: ConsultantContact = {
      id: Date.now().toString(),
      name: '',
      email: '',
      phone: '',
      isPrimary: contacts.length === 0
    };
    setContacts([...contacts, newContact]);
  };

  const updateContact = (id: string, field: keyof ConsultantContact, value: string | boolean) => {
    setContacts(contacts.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const removeContact = (id: string) => {
    setContacts(contacts.filter(c => c.id !== id));
  };

  const setPrimaryContact = (id: string) => {
    setContacts(contacts.map(c => ({ ...c, isPrimary: c.id === id })));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={consultant ? 'Szakreferens szerkesztése' : 'Új szakreferens'}
      size="4xl"
    >
      <form onSubmit={handleSubmit}>
        <div className="p-6 space-y-6">
            {/* Cég adatok */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Cég adatok</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cégnév *
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adószám
                  </label>
                  <input
                    type="text"
                    value={formData.taxNumber}
                    onChange={(e) => setFormData({ ...formData, taxNumber: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cégjegyzékszám
                  </label>
                  <input
                    type="text"
                    value={formData.registrationNumber}
                    onChange={(e) => setFormData({ ...formData, registrationNumber: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cím
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Megjegyzés
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Megjegyzések a cégről..."
                  />
                </div>
              </div>
            </div>

            {/* Kapcsolattartók */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Kapcsolattartók</h3>
                <button
                  type="button"
                  onClick={addContact}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Hozzáadás
                </button>
              </div>

              {contacts.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-sm text-gray-500">Még nincs kapcsolattartó hozzáadva</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {contacts.map((contact, index) => (
                    <div key={contact.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">
                          Kapcsolattartó #{index + 1}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setPrimaryContact(contact.id)}
                            className={`text-xs px-2 py-1 rounded ${
                              contact.isPrimary
                                ? 'bg-blue-100 text-blue-800 font-medium'
                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                            }`}
                          >
                            {contact.isPrimary ? '✓ Elsődleges' : 'Elsődlegesnek jelöl'}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeContact(contact.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <input
                            type="text"
                            placeholder="Név"
                            value={contact.name}
                            onChange={(e) => updateContact(contact.id, 'name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <input
                            type="email"
                            placeholder="Email"
                            value={contact.email}
                            onChange={(e) => updateContact(contact.id, 'email', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <input
                            type="tel"
                            placeholder="Telefonszám"
                            value={contact.phone}
                            onChange={(e) => updateContact(contact.id, 'phone', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <textarea
                          placeholder="Megjegyzés a kapcsolattartóhoz..."
                          value={contact.notes || ''}
                          onChange={(e) => updateContact(contact.id, 'notes', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>ℹ️ Megjegyzés:</strong> A kezelt ügyfelek és meghatalmazások hozzáadása a szakreferens létrehozása után lehetséges a részletes nézetben.
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Mégse
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {loading ? 'Mentés...' : consultant ? 'Frissítés' : 'Létrehozás'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default ConsultantForm;
